import { GraphQLClient } from 'graphql-request';
import { getSdk } from 'generated/graphql';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { GetUserToken, useAuth } from 'components/AuthProvider';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useRef,
} from 'react';

const isDev = process.env.NODE_ENV === 'development';
const graphQlUrl = isDev
  ? process.env.REACT_APP_GRAPHQL_URL_DEV
  : process.env.REACT_APP_GRAPHQL_URL;

const getAuthHeader = (token: string | null) => {

  const header: HeadersInit | undefined = token
    ? { authorization: `Bearer ${token}` }
    : {};

  return header;
};

export const getClientSdk = (token: string | null) => {
  const headers = getAuthHeader(token);
  const client = new GraphQLClient(
    `${isDev ? 'http' : 'https'}://${graphQlUrl}`,
    { headers }
  );

  return getSdk(client);
};

const createSubClient = (getUserToken: GetUserToken) =>
  new SubscriptionClient(`${isDev ? 'ws' : 'wss'}://${graphQlUrl}`, {
    reconnect: true,
    lazy: true,
    connectionParams: async () => {
      const token = await getUserToken();
      return { headers: getAuthHeader(token) };
    },
  });

const subClientContext = createContext<SubscriptionClient | null>(null);

export const SubClientProvider = ({ children }: PropsWithChildren<{}>) => {
  const { getUserToken } = useAuth();

  const clientRef = useRef<SubscriptionClient | null>(null);

  const getClient = () => {
    if (!clientRef.current) clientRef.current = createSubClient(getUserToken);

    return clientRef.current;
  };

  return (
    <subClientContext.Provider value={getClient()}>
      {children}
    </subClientContext.Provider>
  );
};

export const useSubClient = () => {
  const subClient = useContext(subClientContext);

  if (!subClient) throw new Error('Use in SubClientProvider');

  return subClient;
};
